import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  ConfirmModal,
  Select,
  Input,
} from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Add,
  Search,
  Edit,
  Delete,
  Close,
  AdminPanelSettings,
  Warning,
  Person,
  CheckCircle
} from '../../mui-icons';
import './AdminsPage.css';

const AdminsPage = () => {
  const { admin: currentAdmin, canManageAdmins } = useAdminAuth();
  
  // States
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // Create Modal - User Search
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('admin');
  
  // Form States
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Check permission
  if (!canManageAdmins()) {
    return (
      <div className="admins-page">
        <div className="admin-card pn-card pn-card--default">
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>Você não tem permissão para acessar esta página.</p>
            <p>Apenas super_admin pode gerenciar administradores.</p>
          </div>
        </div>
      </div>
    );
  }

  // Load admins
  const loadAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search })
      };

      const response = await adminAPI.getAdmins(params);
      
      if (response.status === 'success') {
        setAdmins(response.data.items || response.data || []);
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total || 0,
            total_pages: response.data.pagination.total_pages || 0
          }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar administradores:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search]);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  // Search users for adding as admin (with debounce)
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) {
      setUserSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const response = await adminAPI.getUsers({ search: userSearch, limit: 10 });
        if (response.status === 'success') {
          // Filter out users that are already admins
          const adminUserIds = admins.map(a => a.idusers);
          const filteredUsers = (response.data.items || []).filter(
            user => !adminUserIds.includes(user.idusers)
          );
          setUserSearchResults(filteredUsers);
        }
      } catch (err) {
        console.error('Erro ao buscar usuários:', err);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [userSearch, admins]);

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setUserSearch('');
    setUserSearchResults([]);
    setSelectedUser(null);
    setSelectedRole('admin');
    setFormError('');
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (adminUser) => {
    setModalMode('edit');
    setSelectedAdmin(adminUser);
    setSelectedRole(adminUser.role || 'admin');
    setFormError('');
    setShowModal(true);
  };

  // Open delete modal
  const openDeleteModal = (adminUser) => {
    setSelectedAdmin(adminUser);
    setShowDeleteModal(true);
  };

  // Select user from search
  const selectUser = (user) => {
    setSelectedUser(user);
    setUserSearch('');
    setUserSearchResults([]);
  };

  // Handle create admin
  const handleCreate = async () => {
    setFormError('');

    if (!selectedUser) {
      setFormError('Selecione um usuário');
      return;
    }

    setFormLoading(true);

    try {
      await adminAPI.createAdmin({
        idusers: selectedUser.idusers,
        role: selectedRole
      });

      setShowModal(false);
      loadAdmins();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Handle update admin role
  const handleUpdate = async () => {
    setFormError('');
    setFormLoading(true);

    try {
      // Usar idadminusers (não idusers) conforme esperado pelo backend
      await adminAPI.updateAdmin(selectedAdmin.idadminusers, {
        role: selectedRole
      });

      setShowModal(false);
      loadAdmins();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modalMode === 'create') {
      await handleCreate();
    } else {
      await handleUpdate();
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setDeleteLoading(true);

    try {
      // Usar idadminusers (não idusers) conforme esperado pelo backend
      await adminAPI.deleteAdmin(selectedAdmin.idadminusers);
      setShowDeleteModal(false);
      loadAdmins();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const roleMap = {
      super_admin: { label: 'Super Admin', class: 'role-super' },
      admin: { label: 'Administrador', class: 'role-admin' },
      viewer: { label: 'Visualizador', class: 'role-viewer' },
      support: { label: 'Suporte', class: 'role-support' }
    };
    const { label, class: className } = roleMap[role] || { label: role, class: '' };
    return <span className={`role-badge ${className}`}>{label}</span>;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <div className="admins-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <AdminPanelSettings sx={{ fontSize: 32 }} />
          <div>
            <h1>Administradores</h1>
            <p>Atribuir permissões administrativas a usuários</p>
          </div>
        </div>
        
        <Button variant="primary" onClick={openCreateModal}>
          <Add sx={{ fontSize: 20 }} />
          Adicionar Admin
        </Button>
      </div>

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="search-box">
            <Search className="search-icon" />
            <input 
              type="text"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>
        </div>
      </div>

      {/* Admins Table */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadAdmins}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th style={{ width: '120px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td><Skeleton width={150} height={20} /></td>
                        <td><Skeleton width={180} height={20} /></td>
                        <td><Skeleton width={100} height={20} /></td>
                        <td><Skeleton width={60} height={20} /></td>
                        <td><Skeleton width={100} height={20} /></td>
                        <td><Skeleton width={80} height={30} /></td>
                      </tr>
                    ))
                  ) : admins.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="empty-state">
                        <AdminPanelSettings sx={{ fontSize: 48, opacity: 0.3 }} />
                        <p>Nenhum administrador encontrado</p>
                      </td>
                    </tr>
                  ) : (
                    admins.map(adminUser => (
                      <tr key={adminUser.idusers || adminUser.id}>
                        <td>
                          <div className="admin-name-cell">
                            <Person sx={{ fontSize: 20, color: 'var(--gray-medium)' }} />
                            <strong>{adminUser.name}</strong>
                          </div>
                        </td>
                        <td>{adminUser.email || adminUser.mail}</td>
                        <td>{getRoleBadge(adminUser.role)}</td>
                        <td>
                          <span className={`status-badge ${adminUser.active !== false ? 'status-success' : 'status-error'}`}>
                            {adminUser.active !== false ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td>{formatDate(adminUser.created_at)}</td>
                        <td>
                          <div className="action-buttons">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(adminUser)}
                              title="Editar Role"
                              aria-label="Editar Role"
                            >
                              <Edit sx={{ fontSize: 18 }} />
                            </Button>
                            {(adminUser.idusers || adminUser.id) !== currentAdmin?.idusers && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteModal(adminUser)}
                                title="Remover"
                                aria-label="Remover"
                              >
                                <Delete sx={{ fontSize: 18 }} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </button>
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.total_pages}
                </span>
                <button 
                  className="pagination-btn"
                  disabled={pagination.page === pagination.total_pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'create' ? 'Adicionar Administrador' : 'Editar Permissão'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={formLoading}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="admin-form"
              loading={formLoading}
              disabled={modalMode === 'create' && !selectedUser}
            >
              {modalMode === 'create' ? 'Adicionar Admin' : 'Salvar'}
            </Button>
          </>
        }
      >
            <form id="admin-form" onSubmit={handleSubmit} className="modal-form">
              {formError && (
                <div className="form-error">
                  <Warning sx={{ fontSize: 18 }} />
                  {formError}
                </div>
              )}

              {/* Create Mode - User Search */}
              {modalMode === 'create' && (
                <div className="form-group">
                  <label className="form-label">Usuário *</label>
                  
                  {selectedUser ? (
                    // Selected user display
                    <div className="selected-user-card">
                      <div className="selected-user-info">
                        <Person sx={{ fontSize: 24 }} />
                        <div>
                          <strong>{selectedUser.name}</strong>
                          <span>{selectedUser.mail}</span>
                        </div>
                      </div>
                      <button 
                        type="button"
                        className="remove-user-btn"
                        onClick={() => setSelectedUser(null)}
                      >
                        <Close sx={{ fontSize: 18 }} />
                      </button>
                    </div>
                  ) : (
                    // User search input
                    <div className="user-search-container">
                      <div className="input-container">
                        <Search className="input-icon" />
                        <input 
                          type="text"
                          className="form-input"
                          placeholder="Buscar usuário por nome ou email..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          disabled={formLoading}
                        />
                      </div>
                      
                      {/* Search Results Dropdown */}
                      {(userSearchResults.length > 0 || userSearchLoading) && (
                        <div className="user-search-results">
                          {userSearchLoading ? (
                            <div className="search-loading">Buscando...</div>
                          ) : (
                            userSearchResults.map(user => (
                              <div 
                                key={user.idusers}
                                className="user-search-item"
                                onClick={() => selectUser(user)}
                              >
                                <Person sx={{ fontSize: 20 }} />
                                <div className="user-search-info">
                                  <strong>{user.name}</strong>
                                  <span>{user.mail}</span>
                                </div>
                              </div>
                            ))
                          )}
                          {!userSearchLoading && userSearchResults.length === 0 && userSearch.length >= 2 && (
                            <div className="search-empty">
                              Nenhum usuário encontrado ou todos já são admins
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <p className="form-hint">
                    O usuário precisa já ter uma conta no aplicativo PersonalNutri
                  </p>
                </div>
              )}

              {/* Edit Mode - Show User Info */}
              {modalMode === 'edit' && selectedAdmin && (
                <div className="form-group">
                  <label className="form-label">Usuário</label>
                  <div className="selected-user-card readonly">
                    <div className="selected-user-info">
                      <Person sx={{ fontSize: 24 }} />
                      <div>
                        <strong>{selectedAdmin.name}</strong>
                        <span>{selectedAdmin.email || selectedAdmin.mail}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Role Selection */}
              <div className="form-group">
                <label className="form-label">Nível de Acesso *</label>
                <div className="input-container">
                  <AdminPanelSettings className="input-icon" />
                  <select
                    className="form-input form-select"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    required
                    disabled={formLoading}
                  >
                    <option value="viewer">Visualizador - Apenas leitura</option>
                    <option value="support">Suporte - Tickets + visualização</option>
                    <option value="admin">Administrador - CRUD completo</option>
                    <option value="super_admin">Super Admin - Acesso total</option>
                  </select>
                </div>
                <div className="role-description">
                  {selectedRole === 'viewer' && 'Pode visualizar dados, sem poder editar ou criar.'}
                  {selectedRole === 'support' && 'Pode gerenciar tickets de suporte e visualizar usuários.'}
                  {selectedRole === 'admin' && 'Pode criar, editar e desativar recursos.'}
                  {selectedRole === 'super_admin' && 'Acesso total, incluindo exclusão permanente e gestão de admins.'}
                </div>
              </div>

            </form>
      </FormModal>

      <ConfirmModal
        open={showDeleteModal}
        title="Remover Administrador"
        message={`Tem certeza que deseja remover as permissões de administrador de ${selectedAdmin?.name}? O usuário continuará com acesso ao aplicativo, apenas perderá as permissões administrativas.`}
        confirmLabel="Remover"
        loading={deleteLoading}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AdminsPage;
